/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSpring, easings, animated } from 'react-spring';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import Checkbox from '@material-ui/core/Checkbox';
import { ThemeProvider } from '@material-ui/core/styles';
import Select from 'react-select';
import checkboxTheme from '../../../shared/theme/checkboxTheme';
import {
  buttonReset,
  focusRing,
  focusWithinRing,
} from '../../../shared/theme/focusStyles';
import { FOCUSABLE_SELECTOR, handleCheckboxEnter } from '../../../shared/utils/focusHelpers';
import { subscribeKeyboardNav } from '../../../shared/utils/keyboardNavMode';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import SelectDropdownExercisePickerLocal from '../components/presentational/SelectDropdownExercisePickerLocal';
import ExerciseTableTransposedLocal from '../components/presentational/ExerciseTableTransposedLocal';
import ExerciseTableTransposedSAQCLocal from '../components/presentational/ExerciseTableTransposedSAQCLocal';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import TBToggleSwitch from '../../../shared/components/TBToggleSwitch/TBToggleSwitch';
import { updateFutureOccurrencesProgrammedCalendarItem, fetchFutureOccurrencesCount } from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';
import decodeHtmlEntities from '../../../shared/utils/decodeHtmlEntities';

// Create custom SVG icons for the checkboxes
const CustomUncheckedIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke='#E0E0E0' />
  </svg>
);

const CustomCheckedIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill='white' stroke='#77A7FF' />
    <path d='M5 9L8 12L13 6' stroke='#77A7FF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

// Styled Checkbox component
const StyledCheckbox = styled(Checkbox)`
  padding: 4px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

// Styled Components
const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};

  /* Phones: suppress the date-picker calendar popup - the on-screen keyboard
     covers it anyway, and typing the date is the more useful interaction at
     this size. The input itself still works. Scoped to this modal. */
  @media (max-width: 480px) {
    .react-datepicker-popper {
      display: none !important;
    }
  }
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background: ${(props) => (props.disabled ? '#E0E0E0' : '#444444')};
  color: ${(props) => (props.disabled ? '#9E9E9E' : 'white')};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  border: ${(props) => (props.disabled ? '1px solid #E0E0E0' : '1px solid #444444')};
  margin-bottom: 8px;
  ${focusRing}
`;

const SecondaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background: white;
  color: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
  ${focusRing}
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
`;

const ContentSection = styled('div')`
  margin-bottom: 24px;
`;

const SectionTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 16px;
`;

const ExerciseNameDisplay = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #444444;
  background: #F5F5F5;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #E0E0E0;
`;

const DateLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 8px;
  display: block;
`;

const DatePickerRow = styled('div')`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 50%;
`;

/* Wraps only the date field so the focus border hugs the input and its calendar icon rather
   than enclosing the label above it. addTopMargin is available for callers that need the gap
   under a label; the pickers here get theirs from DateLabel's own margin-bottom. */
const PickerFocusWrap = styled('div')`
  position: relative;
  margin-top: ${(props) => (props.addTopMargin ? '8px' : '0')};
  ${focusWithinRing}
`;

const SlideContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
`;

const BreadcrumbContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin: 0;
  border-bottom: 1px solid #EEEEEE;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 0px 12px;
    padding-bottom: 12px;
    gap: 4px;
  }

  /* Phones: the modal's content padding already insets the breadcrumbs;
     drop the extra horizontal padding to give the labels room. */
  @media (max-width: 480px) {
    padding: 0px;
    padding-bottom: 12px;
  }
`;

/* A real button so mouse users keep the same behavior and the control carries proper
   semantics. It is kept out of the tab order at the call site: BACK is the keyboard path
   between steps, and the breadcrumb is the first thing in the DOM on every slide, so leaving
   it tabbable would put it ahead of the first editable input. */
const BreadcrumbStep = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  ${focusRing}

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const BreadcrumbNumber = styled('div')`
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 600;
  background: ${(props) => (props.completed || props.active ? '#0DCC8A' : 'white')};
  color: ${(props) => (props.completed || props.active ? 'white' : '#444444')};
  border: ${(props) => {
    if (props.completed || props.active) return 'none';
    return '2px solid #757575';
  }};
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    font-size: 14px;
  }

  /* Phones: drop the numbered bubbles - they push the breadcrumbs off-screen.
     Step state is still conveyed by the label font weight. */
  @media (max-width: 480px) {
    display: none;
  }
`;

const BreadcrumbText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(props) => (props.active || props.completed ? '600' : '400')};
  color: #444444;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  /* Phones: with the bubbles hidden the labels are all that's left -
     let them wrap so all steps fit the modal width. */
  @media (max-width: 480px) {
    white-space: normal;
  }
`;

const BreadcrumbArrow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  transform: rotate(-90deg);
`;

const ExercisePickerContainer = styled('div')`
  margin-bottom: 16px;
  margin-top: 24px;
`;

const TextColumn = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const CheckboxGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  padding: 16px 0;
`;

const ConfirmationText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.6;
  text-align: center;
  padding: 24px 0;
`;

const HighlightText = styled('span')`
  font-weight: 700;
  color: #444444;
`;

const ToggleSection = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0 8px 0;
`;

const ToggleLabelText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 400;
  color: #666666;
`;

/* A fieldset so that disabling is real. `pointer-events: none` blocked only the mouse, so
   Tab still walked into the greyed-out table and every keystroke still wrote to tableState --
   which buildSubmissionObject reads regardless of the toggle. `fieldset[disabled]` natively
   disables every descendant form control, taking them out of the tab order and stopping the
   edits, and it keeps working as table rows are added.

   The resets undo the UA fieldset chrome; min-width: 0 defeats its default
   min-inline-size: min-content, which would otherwise let wide tables force overflow. */
const ToggleControlledSection = styled('fieldset')`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
  opacity: ${(props) => (props.disabled ? '0.4' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  transition: opacity 0.3s ease;
`;

const AdditionalInfoTextArea = styled('textarea')`
  width: 100%;
  min-height: 80px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 10px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #444444;
  }

  &::placeholder {
    color: #9E9E9E;
  }
`;

const CharacterCount = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
  text-align: right;
  margin-top: 4px;
`;

const SelectContainer = styled('div')`
  margin-bottom: 16px;

  .react-select__control {
    border: 1px solid #EEEEEE;
    border-radius: 4px;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: #444444;
    }
  }

  .react-select__control--is-focused {
    border-color: #444444;
    box-shadow: none;
  }

  /* react-select's own focus ring is killed by the box-shadow: none above, so give the
     control the same blue border as every other field. Gated on keyboard nav because the
     focusable element react-select renders is an <input>, and :focus-visible matches those
     even for mouse clicks. */
  [data-nav-mode='keyboard'] & .react-select__control--is-focused {
    border-color: #77A7FF;
    box-shadow: 0 0 0 1px #77A7FF;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: #757575;
  }

  .react-select__menu {
    border: 1px solid #EEEEEE;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .react-select__option {
    cursor: pointer;
  }

  .react-select__option--is-focused {
    background-color: #F5F5F5;
  }

  .react-select__option--is-selected {
    background-color: #444444;
    color: white;
  }
`;

const UpdateFutureOccurrencesModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  // Exercise data for picker
  const allExercises = useSelector((state) => state.calendar.data.allExercises || []);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount || []);

  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const futureOccurrencesCount = useSelector((state) => state.calendar.data.futureOccurrencesCount);

  /** Everything the transposed table owns: the per-set rows plus the
   * item-level properties it exposes (rest, tempo, units, tracking enums)
   * and which property rows are switched on. The table pushes its whole
   * shape up on every edit; we only read it when building the payload. */
  const [tableState, setTableState] = useState(null);

  // Additional options checkboxes
  const [trackVolumeLoad, setTrackVolumeLoad] = useState(modalData?.content?.trackVolumeLoad || false);
  const [trackRepCount, setTrackRepCount] = useState(modalData?.content?.trackRepCount || false);
  const [forcePRUpdate, setForcePRUpdate] = useState(modalData?.content?.forceMaxUpdate || false);
  const [disablePRUpdate, setDisablePRUpdate] = useState(modalData?.content?.maxTrackingDisabled || false);
  const [completionOnly, setCompletionOnly] = useState(modalData?.content?.completionOnly || false);
  const [coachCompletion, setCoachCompletion] = useState(modalData?.content?.coachCompletion || false);
  const [eachSide, setEachSide] = useState(modalData?.content?.eachSide || false);

  // Toggle states for controlling updateSets and updateAdvancedInfo
  const [updateSetsToggle, setUpdateSetsToggle] = useState(false);
  const [updateAdvancedInfoToggle, setUpdateAdvancedInfoToggle] = useState(false);
  const [additionalInfoText, setAdditionalInfoText] = useState(
    decodeHtmlEntities(modalData?.content?.additionalInfo || ''),
  );

  // Exercise picker state
  const [selectedExercise, setSelectedExercise] = useState(null);
  const textWrapperRef = useRef(null);

  const exerciseType = modalData?.content?.type;
  const isLift = exerciseType === 'lift';
  const isTag = exerciseType === 'tag';
  const isSAQC = exerciseType === 'saq+c';
  const isCircuit = exerciseType === 'circuit';
  const showAdditionalSection = isLift || isTag || isSAQC || isCircuit;

  // Slide management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hideOverflow, setHideOverflow] = useState(true);
  const [isFetchingCount, setIsFetchingCount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** The picker is seeded from the item exactly once. This effect re-runs when
   * the exercise/tag lists arrive (they load async) and every time the user
   * re-enters slide 2, so without the latch a BACK and forward would throw away
   * an exercise the user picked. The latch is set where we actually seed, not
   * at mount — on the first pass the lists can still be empty. */
  const hasSeededExercise = useRef(false);

  // Initialize selected exercise from modalData
  useEffect(() => {
    if (currentSlide === 1 && !hasSeededExercise.current) {
      // Handle tag exercises
      if (modalData?.content?.type === 'tag' && modalData?.content?.tagId && tagsForAccount.length > 0) {
        hasSeededExercise.current = true;
        const tag = tagsForAccount.find(
          (t) => t.id === modalData?.content?.tagId,
        );

        if (tag) {
          setSelectedExercise({
            name: modalData?.content?.exerciseName || tag.name,
            id: tag.id,
            tagId: tag.id,
            type: 'tag',
            ...tag,
          });
        } else {
          // Tag not found in list - use modalData
          setSelectedExercise({
            name: modalData?.content?.exerciseName || 'Unknown Tag',
            id: modalData?.content?.tagId,
            tagId: modalData?.content?.tagId,
            type: 'tag',
          });
        }
      } else if (modalData?.content?.exerciseId && allExercises.length > 0) {
        hasSeededExercise.current = true;
        // Handle regular exercises
        const exercise = allExercises.find(
          (ex) => ex.id === modalData?.content?.exerciseId,
        );

        if (exercise) {
          setSelectedExercise({
            name: modalData?.content?.exerciseName || exercise.name,
            id: exercise.id,
            exerciseId: exercise.id,
            type: exercise.type,
            ...exercise,
          });
        } else {
          // Exercise not found in list - use modalData
          setSelectedExercise({
            name: modalData?.content?.exerciseName || 'Unknown Exercise',
            id: modalData?.content?.exerciseId,
            exerciseId: modalData?.content?.exerciseId,
            type: modalData?.content?.type,
          });
        }
      }
    }
  }, [currentSlide, modalData, allExercises, tagsForAccount]);

  // Occurrence mode state
  const occurrenceOptions = [
    { value: 'all', label: 'Update all future occurrences (Default)' },
    { value: 'custom', label: 'Custom date range' },
  ];
  const [occurrenceMode, setOccurrenceMode] = useState(occurrenceOptions[0]);

  // Date range state
  const [startDate, setStartDate] = useState(
    modalData?.date ? moment(modalData?.date).toDate() : new Date(),
  );
  const [startDateString, setStartDateString] = useState(
    modalData?.date ? modalData?.date : moment().format('YYYY-MM-DD'),
  );

  const [endDate, setEndDate] = useState(null);
  const [endDateString, setEndDateString] = useState('');

  // Clear end date when switching from custom to all mode
  useEffect(() => {
    if (occurrenceMode.value === 'all') {
      setEndDate(null);
      setEndDateString('');
    }
  }, [occurrenceMode]);

  // Animation setup
  const showSlide1 = currentSlide === 0;
  const showSlide2 = currentSlide === 1;
  const showSlide3 = currentSlide === 2;

  /** Slide 2 mounts on its first visit and is never unmounted again — it is
   * hidden with `display: none` instead. The transposed tables seed ALL of
   * their internal state from `content` on mount (rows, which property rows are
   * switched on, rest/tempo/velocity/peak power, and the load<->bodyweight and
   * reps<->time renames) and expose no way to be re-seeded. Unmounting on the
   * way to slide 3 and remounting on BACK would restore the original item, and
   * the table's mount-time `onChange` would overwrite `tableState` with those
   * pre-edit values — so CONFIRM would write the originals over every future
   * occurrence. Latched in render rather than in an effect so the mount lands
   * in the same commit as the slide change and the entrance animation isn't
   * clipped. Latched on visit rather than on `currentSlide >= 1` because the
   * breadcrumb can send the user back to slide 1. */
  const hasVisitedSlide2 = useRef(false);
  if (showSlide2) {
    hasVisitedSlide2.current = true;
  }

  /* Mirror the tracked input modality onto the container so CSS can show focus rings only to
     keyboard users. Written imperatively via the ref because this fires on every keystroke
     and click, and re-rendering this component that often would be wasteful. */
  const modalContainerRef = useRef(null);

  useEffect(() => subscribeKeyboardNav((usingKeyboard) => {
    if (modalContainerRef.current) {
      modalContainerRef.current.dataset.navMode = usingKeyboard ? 'keyboard' : 'mouse';
    }
  }), []);

  /* Slide content refs, used to move focus into the step being shown.

     Indexed by currentSlide rather than "first non-null ref": slide 2 is latched mounted by
     hasVisitedSlide2 and only hidden with display:none, so more than one of these is
     non-null once it has been visited. */
  const slide1Ref = useRef(null);
  const slide2Ref = useRef(null);
  const slide3Ref = useRef(null);
  const slideRefs = [slide1Ref, slide2Ref, slide3Ref];

  const getActiveSlide = () => slideRefs[currentSlide]?.current;

  const hasNavigatedRef = useRef(false);

  /* On a step change this stops focus falling to <body> when the activated NEXT/BACK button
     unmounts, which would make the next Tab restart from the top of the document.

     Deliberately skipped on open: nothing should be focused until the user presses Tab, and
     because the breadcrumb is not tabbable the first Tab lands on the first editable input
     on its own. */
  useEffect(() => {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }

    const container = getActiveSlide();
    if (!container) {
      return;
    }

    const target = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      // Skip the footer so Enter can't immediately re-submit, and skip date-picker inputs,
      // which pop their calendar open on focus.
      .find((el) => !el.closest('[data-modal-footer]') && !el.classList.contains('date-picker'));

    /* preventScroll because this runs while ModalContainer is overflow: hidden for the slide
       transition, and native focus scrolling walks clipped ancestors too. The target is the
       first control of a fresh step, so it is already in view. */
    (target || container).focus({ preventScroll: true });
  }, [currentSlide]);

  /* fieldset[disabled] cannot reach a div[role=button], and SwapPropDropdown's trigger inside
     the tables is one. Sweeping tabindex is cheaper than threading a disabled prop through a
     component the non-Local tables also use. */
  useEffect(() => {
    const section = slide2Ref.current;
    if (!section) {
      return;
    }
    section.querySelectorAll('[data-toggle-section] [role="button"]').forEach((el) => {
      el.setAttribute('tabindex', el.closest('[data-toggle-section]')?.disabled ? '-1' : '0');
    });
  }, [updateSetsToggle, updateAdvancedInfoToggle, tableState, currentSlide]);

  /* react-select 2.x opens on Space and the arrow keys but deliberately ignores Enter while
     closed, so Enter is added here. Reading menuIsOpen back means open state is controlled. */
  const [isOccurrenceMenuOpen, setIsOccurrenceMenuOpen] = useState(false);

  const handleOccurrenceKeyDown = (e) => {
    if (e.key === 'Enter' && !isOccurrenceMenuOpen) {
      /* react-select checks defaultPrevented before running its own handler, so this cleanly
         takes over rather than racing it. */
      e.preventDefault();
      setIsOccurrenceMenuOpen(true);
    }
  };

  /* Moves focus to the first control after a date picker, so finishing with one date hands the
     user straight to the next field instead of stranding them on a closed calendar.

     Date-picker inputs are deliberately not skipped: landing on End Date with its calendar
     open is the point. The footer is included too -- on the last field NEXT genuinely is the
     next control, and that cannot cause an accidental submit, because the day cell consumes
     the Enter keydown and activating the button needs a second, separate press. */
  const focusFieldAfter = (wrapper) => {
    const container = getActiveSlide();
    if (!container) {
      return;
    }
    const next = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .find((el) => !wrapper.contains(el)
        // eslint-disable-next-line no-bitwise
        && (wrapper.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
    next?.focus();
  };

  /* Only one date picker can be open at a time, so a single flag covers both. */
  const escapeBelongsToDatePickerRef = useRef(false);

  const handleDatePickerKeyDownCapture = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      escapeBelongsToDatePickerRef.current = !!e.currentTarget.querySelector('.react-datepicker-popper');
    }
  };

  /* Without this, Escape inside an open calendar bubbles to react-modal and closes the whole
     modal, discarding everything the user configured. */
  const handleDatePickerKeyDown = (e) => {
    if (!escapeBelongsToDatePickerRef.current) {
      return;
    }
    if (e.key === 'Escape') {
      escapeBelongsToDatePickerRef.current = false;
      e.stopPropagation();
      /* Focus deliberately stays put. Escape is a dismiss gesture, and advancing would land
         the user in the NEXT field -- whose calendar opens on focus, so escaping one calendar
         would immediately open another. */
    }
    if (e.key === 'Enter') {
      // react-datepicker has already applied the date and closed the calendar by now.
      escapeBelongsToDatePickerRef.current = false;
      focusFieldAfter(e.currentTarget);
    }
  };

  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide1) {
        setHideOverflow(false);
      }
    },
  });

  let slide2Transform;
  if (showSlide2) {
    slide2Transform = 'translateX(0%)';
  } else if (currentSlide < 1) {
    slide2Transform = 'translateX(100%)';
  } else {
    slide2Transform = 'translateX(-100%)';
  }

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: slide2Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide2) {
        setHideOverflow(false);
      }
    },
  });

  let slide3Transform;
  if (showSlide3) {
    slide3Transform = 'translateX(0%)';
  } else if (currentSlide < 2) {
    slide3Transform = 'translateX(100%)';
  } else {
    slide3Transform = 'translateX(-100%)';
  }

  const slide3Animation = useSpring({
    opacity: showSlide3 ? 1 : 0,
    transform: slide3Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide3) {
        setHideOverflow(false);
      }
    },
  });

  const exerciseIdFinder = () => {
    if (selectedExercise) {
      if (selectedExercise?.exerciseId) {
        return selectedExercise.exerciseId;
      }

      if (selectedExercise?.tagId) {
        return selectedExercise.tagId;
      }
    }
    return null;
  };

  const buildSubmissionObject = () => {
    // The transposed table hands us finished per-set rows — no CSV to parse,
    // so the only gate left is that reps are actually filled in. Circuits get
    // no table on slide 2, so fall back to the item's existing sets rather
    // than shipping an empty array that would wipe them.
    const sets = (tableState?.sets || modalData?.content?.sets || [])
      .map((set, i) => ({ ...set, setNumber: i + 1 }));
    const rowSwitch = tableState?.primaryRowSwitch || {};
    const hasOriginalSets = modalData?.content?.sets && modalData?.content?.sets.length > 0;
    const isSetOption = modalData?.content?.type === 'lift'
      || modalData?.content?.type === 'tag'
      || (modalData?.content?.type === 'saq+c' && hasOriginalSets)
      || (modalData?.content?.type === 'circuit' && hasOriginalSets);

    if (updateSetsToggle && (isLift || isTag)) {
      const everySetHasReps = sets.length > 0 && sets.every(
        (set) => set.reps !== null && set.reps !== undefined && set.reps !== '',
      );
      if (!everySetHasReps) {
        showErrorToast('Reps field cannot be empty');
        return null;
      }
    }

    // Build main submission object
    // Use selectedExercise if available (user may have changed it), otherwise fall back to modalData
    const submissionObject = {
      assignedId: modalData?.content?.assignedId,
      originalExerciseId: modalData?.content?.exerciseId || modalData?.content?.tagId,
      order: modalData?.content?.order,
      groupingLetter: modalData?.content?.groupingLetter,
      type: modalData?.content?.type,
    };

    // Per-type tracking flags — only ship fields that the UI actually exposes
    // so stale state can't persist to fields the user can't see/change here.
    if (isLift || isTag) {
      submissionObject.coachCompletion = coachCompletion;
      submissionObject.completionOnly = completionOnly;
      submissionObject.eachSide = eachSide;
      submissionObject.forceMaxUpdate = forcePRUpdate;
      submissionObject.maxTrackingDisabled = disablePRUpdate;
    } else if (isSAQC) {
      submissionObject.coachCompletion = coachCompletion;
      submissionObject.completionOnly = completionOnly;
    } else if (isCircuit) {
      submissionObject.coachCompletion = coachCompletion;
    }

    if (modalData?.content?.description) {
      submissionObject.description = decodeHtmlEntities(modalData.content.description);
    }

    if (isSetOption) {
      submissionObject.sets = sets;
    }

    // Only add sets-related properties for lift/tag types
    if (isSetOption && (modalData?.content?.type === 'lift' || modalData?.content?.type === 'tag')) {
      submissionObject.trackRepCount = trackRepCount;
      submissionObject.trackVolumeLoad = trackVolumeLoad;
      submissionObject.repsOnly = !!tableState?.isBodyweight;
      submissionObject.repType = tableState?.isTime ? 'time' : 'reps';
    }

    // Add repIntensityUnit for saq+c
    if (modalData?.content?.type === 'saq+c' && tableState?.intensityUnit) {
      submissionObject.repIntensityUnit = tableState.intensityUnit;
    }

    // Add repRestUnit for saq+c
    if (modalData?.content?.type === 'saq+c' && tableState?.restPerRepUnit) {
      submissionObject.repRestUnit = tableState.restPerRepUnit;
    }

    if (selectedExercise) {
      if (modalData?.content.type === 'tag') {
        submissionObject.tagId = exerciseIdFinder();
      } else {
        submissionObject.exerciseId = exerciseIdFinder();
      }
    }

    if (Object.keys(selectedReduxCalendar).length) {
      // If working from a parent calendar, use the parentId for the assignment's calendarId
      // The top-level calendarId will still use selectedReduxCalendar.id (handled in the action)
      if (modalData.isInherited && modalData.parentId) {
        submissionObject.calendarId = modalData.parentId;
      } else {
        submissionObject.calendarId = selectedReduxCalendar.id;
      }
    } else if (Object.keys(selectedReduxAthlete).length) {
      if (modalData.isInherited && modalData.parentId) {
        submissionObject.calendarId = modalData.parentId;
      } else {
        submissionObject.userId = selectedReduxAthlete.id;
      }
    }

    // Add optional fields if they have values
    if (tableState?.rest && rowSwitch.rest) {
      submissionObject.restTime = tableState.rest;
    }

    if (tableState?.tempo && rowSwitch.tempo) {
      submissionObject.tempo = tableState.tempo;
    }

    // Velocity and Peak Power exist only in the lift/tag table. The saq+c table
    // emits a primaryRowSwitch with no `velocity` / `peak power` keys and a
    // circuit renders no table at all (tableState stays null), so deriving
    // these from the switch for those types would always ship
    // trackPeakPower: 0 / trackBarSpeed: false and clear whatever the future
    // occurrences already had. Omitted means "leave unchanged" to the backend,
    // so only the types whose UI can express a change send them.
    //
    // Within lift/tag: drive the payload from the switch alone so add/delete
    // both propagate. The value state isn't seeded when the user adds via the +
    // button, and the backend treats omitted trackBarSpeed/trackPeakPower as
    // "leave unchanged" (unlike restTime), so we have to send explicit
    // clear values when the switch is off.
    if (isLift || isTag) {
      submissionObject.trackPeakPower = rowSwitch['peak power']
        ? (tableState?.peakPowerEnum || 1)
        : 0;
      submissionObject.trackBarSpeed = !!rowSwitch.velocity;
    }

    // Add date range fields
    submissionObject.startDate = startDateString;
    // Only include endDate when custom date range is selected
    if (occurrenceMode.value === 'custom' && endDateString) {
      submissionObject.endDate = endDateString;
    }

    // Add boolean flags
    submissionObject.updateExercise = !!(selectedExercise && exerciseIdFinder() !== (modalData?.content?.exerciseId || modalData?.content?.tagId));

    // The Velocity / Peak Power columns live in Slide 2 (the "Update Sets" toggle),
    // but the backend treats trackBarSpeed / trackPeakPower as "advanced info" —
    // gated by updateAdvancedInfo. Auto-enable that flag if the user changed
    // either, so the backend actually applies the change. The other advanced-info
    // fields are already sent at their current values, so flipping this gate on
    // doesn't replace anything the user didn't intentionally change. Only lift
    // and tag can reach this state; for the other types nothing was sent above,
    // so there is nothing to gate.
    let trackingFlagsChanged = false;
    if (isLift || isTag) {
      trackingFlagsChanged = submissionObject.trackBarSpeed !== !!modalData?.content?.trackBarSpeed
        || submissionObject.trackPeakPower !== (modalData?.content?.trackPeakPower || 0);
    }

    // Toggle-driven flags (user explicitly controls what gets updated)
    submissionObject.updateSets = updateSetsToggle;
    submissionObject.updateAdvancedInfo = updateAdvancedInfoToggle || trackingFlagsChanged;

    // Only include additionalInfo if it has content
    if (additionalInfoText && additionalInfoText.trim() !== '') {
      submissionObject.additionalInfo = additionalInfoText;
    }

    return submissionObject;
  };

  // Navigation handlers
  const handleNext = async () => {
    // Slide 1 validation
    if (currentSlide === 0) {
      /* Validation: Start date is required. Checked for validity, not just emptiness --
         a truthy-but-invalid value would otherwise pass here and then pass the range check
         below too, since isBefore() on an invalid moment returns false. */
      if (!startDateString || !moment(startDateString).isValid()) {
        showErrorToast('Please select a start date');
        return;
      }

      // Validation: Start date cannot be before exercise date
      if (moment(startDateString).isBefore(moment(modalData?.date), 'day')) {
        showErrorToast('Start date cannot be before the exercise date');
        return;
      }
    }

    // Slide 2 validation and fetch count
    if (currentSlide === 1) {
      // The table clamps its own set count to 1-10, so just confirm rows exist.
      if (updateSetsToggle && !(tableState?.sets || []).length) {
        showErrorToast('Add at least one set');
        return;
      }

      const submissionObject = buildSubmissionObject();
      if (!submissionObject) {
        return; // Error already shown
      }

      // Validate at least one update option is enabled
      if (!submissionObject.updateSets && !submissionObject.updateAdvancedInfo && !submissionObject.updateExercise) {
        showErrorToast('Please enable at least one update option');
        return;
      }

      setIsFetchingCount(true);

      await dispatch(fetchFutureOccurrencesCount(
        currentUser.accountCode,
        modalData?.content?.assignedId,
        modalData?.content?.exerciseId || modalData?.content?.tagId,
        submissionObject.startDate,
        submissionObject.endDate,
      ));

      setIsFetchingCount(false);
    }

    setHideOverflow(true);
    if (currentSlide < 2) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handleBack = () => {
    setHideOverflow(true);
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    // CONFIRM is keyboard-activatable now, so guard against a second Enter double-dispatching.
    if (isSubmitting) {
      return;
    }
    const submissionObject = buildSubmissionObject();
    if (!submissionObject) {
      return; // Error already shown
    }

    const formattedDate = moment(modalData?.date).format('YYYY-MM-DD');
    setIsSubmitting(true);

    dispatch(updateFutureOccurrencesProgrammedCalendarItem(
      currentUser.accountCode,
      submissionObject,
      formattedDate,
      exerciseIdFinder(),
      modalData?.content?.type === 'tag',
      {
        onSuccess: () => {
          closeModal();
        },
        onError: () => {
          // Re-enable so the user can correct the problem and retry.
          setIsSubmitting(false);
        },
      },
    ));
  };

  // Handler for exercise selection changes
  const handleExerciseChange = useCallback((newExercise) => {
    if (!newExercise) {
      // Prevent clearing selection
      return;
    }

    // A manual pick outranks the initial seed, even if the exercise/tag lists
    // only resolve afterwards.
    hasSeededExercise.current = true;

    // Update selected exercise - will be used in submission object
    setSelectedExercise(newExercise);
  }, []);

  // Breadcrumb click handler
  const handleBreadcrumbClick = (step) => {
    // Only allow clicking on completed steps or current step
    if (step <= currentSlide) {
      setHideOverflow(true);
      setCurrentSlide(step);
    }
  };

  // Render breadcrumb navigation
  const renderBreadcrumb = () => {
    const steps = [
      { number: 1, text: 'Exercise to Update', slideIndex: 0 },
      { number: 2, text: 'Edits and Updates', slideIndex: 1 },
      { number: 3, text: 'Confirmation', slideIndex: 2 },
    ];

    return (
      <BreadcrumbContainer>
        {steps.map((step, index) => {
          const isActive = step.slideIndex === currentSlide;
          const isCompleted = step.slideIndex < currentSlide;
          const isClickable = step.slideIndex <= currentSlide;

          return (
            <React.Fragment key={step.number}>
              <BreadcrumbStep
                aria-current={isActive ? 'step' : undefined}
                clickable={isClickable}
                disabled={!isClickable}
                onClick={() => handleBreadcrumbClick(step.slideIndex)}
                /* Mouse-only: BACK already gives keyboard users this path, and the breadcrumb
                   is first in the DOM on every slide, so leaving it tabbable would put it
                   ahead of the first editable input. */
                tabIndex={-1}
                type='button'
              >
                <BreadcrumbNumber
                  active={isActive}
                  completed={isCompleted}
                >
                  {isCompleted ? (
                    <IcomoonReact
                      iconSet={iconSet}
                      icon='checkmark'
                      size={20}
                      color='white'
                    />
                  ) : (
                    step.number
                  )}
                </BreadcrumbNumber>
                <BreadcrumbText
                  active={isActive}
                  completed={isCompleted}
                >
                  {step.text}
                </BreadcrumbText>
              </BreadcrumbStep>
              {index < steps.length - 1 && (
                <BreadcrumbArrow>
                  <IcomoonReact
                    iconSet={iconSet}
                    icon='gs-arrow-down'
                    size={12}
                    color='#9E9E9E'
                  />
                </BreadcrumbArrow>
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbContainer>
    );
  };

  // Get exercise name for display
  const getExerciseName = () => {
    if (modalData?.content?.exerciseName) {
      return modalData?.content?.exerciseName;
    }
    if (modalData?.content?.exercise?.name) {
      return modalData?.content?.exercise.name;
    }
    if (modalData?.content?.title) {
      return modalData?.content?.title;
    }
    return 'Unknown Exercise';
  };

  // Handler for start date changes
  const handleStartDateChange = (date) => {
    /* Clearing the input calls this with null. Formatting that would store the literal
       string 'Invalid date', which is non-empty and so slips past the !startDateString
       check in handleNext, while isBefore() on an invalid moment returns false and defeats
       the range check too -- shipping startDate: 'Invalid date' to the API. Guarded in the
       same shape handleEndDateChange below already uses. */
    if (!date || !moment(date).isValid()) {
      setStartDate(null);
      setStartDateString('');
      return;
    }
    const momentDate = moment(date);
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    // If end date exists and is before new start date, clear it
    if (endDate && moment(endDate).isBefore(momentDate, 'day')) {
      setEndDate(null);
      setEndDateString('');
    }
  };

  // Handler for end date changes
  const handleEndDateChange = (date) => {
    if (date) {
      const momentDate = moment(date);
      setEndDate(momentDate.toDate());
      setEndDateString(momentDate.format('YYYY-MM-DD'));
    } else {
      setEndDate(null);
      setEndDateString('');
    }
  };

  return (
    <ModalContainer
      data-nav-mode='mouse'
      hideOverflow={hideOverflow}
      onKeyDown={handleCheckboxEnter}
      ref={modalContainerRef}
    >
      {renderBreadcrumb()}

      {showSlide1 && (
        <animated.div style={slide1Animation}>
          <SlideContent ref={slide1Ref} tabIndex={-1}>
            {/* Section 1: Exercise to Update */}
            <ContentSection style={{
              marginTop: 24,
            }}
            >
              <SectionTitle>Exercise to Update</SectionTitle>
              <ExerciseNameDisplay>
                {getExerciseName()}
              </ExerciseNameDisplay>
            </ContentSection>

            {/* Section 2: Occurrence Selection */}
            <ContentSection>
              <SectionTitle>Occurrences</SectionTitle>

              <SelectContainer>
                <Select
                  options={occurrenceOptions}
                  value={occurrenceMode}
                  onChange={(option) => {
                    setOccurrenceMode(option);
                    setIsOccurrenceMenuOpen(false);
                  }}
                  className='react-select'
                  classNamePrefix='react-select'
                  isSearchable={false}
                  menuIsOpen={isOccurrenceMenuOpen}
                  onMenuOpen={() => setIsOccurrenceMenuOpen(true)}
                  onMenuClose={() => setIsOccurrenceMenuOpen(false)}
                  onKeyDown={handleOccurrenceKeyDown}
                />
              </SelectContainer>

              {/* Show date pickers only when custom date range is selected */}
              {occurrenceMode.value === 'custom' && (
                <DatePickerRow>
                  {/* Start Date Picker */}
                  <DatePickerWrapper
                    onKeyDown={handleDatePickerKeyDown}
                    onKeyDownCapture={handleDatePickerKeyDownCapture}
                  >
                    <DateLabel>Start Date</DateLabel>
                    <PickerFocusWrap>
                      <TBDatePicker
                        activeDate={startDateString}
                        setActiveDate={handleStartDateChange}
                        additionalStyles={{
                          margin: '0',
                          maxWidth: '100%',
                        }}
                        showDropShadow={false}
                        showIcon
                        minDate={moment(modalData?.date).toDate()}
                        maxDate={null}
                      />
                    </PickerFocusWrap>
                  </DatePickerWrapper>

                  {/* End Date Picker */}
                  <DatePickerWrapper
                    onKeyDown={handleDatePickerKeyDown}
                    onKeyDownCapture={handleDatePickerKeyDownCapture}
                  >
                    <DateLabel>End Date</DateLabel>
                    <PickerFocusWrap>
                      <TBDatePicker
                        activeDate={endDateString}
                        setActiveDate={handleEndDateChange}
                        additionalStyles={{
                          margin: '0',
                          maxWidth: '100%',
                        }}
                        showDropShadow={false}
                        showIcon
                        minDate={startDate ? moment(startDate).add(1, 'day').toDate() : moment(modalData?.date).add(1, 'day').toDate()}
                        maxDate={null}
                      />
                    </PickerFocusWrap>
                  </DatePickerWrapper>
                </DatePickerRow>
              )}
            </ContentSection>

            {/* Button Container */}
            <ButtonsContainer data-modal-footer>
              <PrimaryButton
                onClick={handleNext}
                disabled={(occurrenceMode.value === 'custom' && !endDateString)
                  || !moment(startDateString).isValid()}
                type='button'
              >
                NEXT
              </PrimaryButton>
              <SecondaryButton onClick={closeModal} type='button'>CANCEL</SecondaryButton>
            </ButtonsContainer>
          </SlideContent>
        </animated.div>
      )}
      {hasVisitedSlide2.current && (
        <animated.div style={{ ...slide2Animation, display: showSlide2 ? undefined : 'none' }}>
          <div ref={slide2Ref} tabIndex={-1}>
            {/* Exercise Picker Section */}
            <ExercisePickerContainer>
              <SectionTitle>Exercise</SectionTitle>
              <TextColumn ref={textWrapperRef}>
                <SelectDropdownExercisePickerLocal
                  listItems={modalData?.content?.type === 'tag' ? tagsForAccount : allExercises}
                  textWrapperRef={textWrapperRef}
                  defaultValue={selectedExercise}
                  selectedOption={selectedExercise}
                  onExerciseChange={handleExerciseChange}
                  fullWidthInput
                  typeToFilter={modalData?.content?.exercise?.type}
                  isTags={modalData?.content?.type === 'tag'}
                  placeholder='Select exercise...'
                />
              </TextColumn>
            </ExercisePickerContainer>
            {/* Toggle 1: Update Exercise Properties */}
            {(modalData?.content?.type === 'lift' || modalData?.content?.type === 'tag' || modalData?.content?.type === 'saq+c') && (
            <ToggleSection>
              <TBToggleSwitch
                ariaLabel='Update exercise properties of future occurrences'
                checked={updateSetsToggle}
                onChange={() => setUpdateSetsToggle(!updateSetsToggle)}
              />
              <ToggleLabelText>
                Toggle on to update exercise properties of future occurrences
              </ToggleLabelText>
            </ToggleSection>
            )}
            {/* Exercise properties table — the standard saved transposed layout:
            * properties as rows, one input per set. Values are entered per
            * cell, so there is no comma-separated shortcut. */}
            <ToggleControlledSection data-toggle-section disabled={!updateSetsToggle}>
              {(isLift || isTag) && (
              <ExerciseTableTransposedLocal
                content={modalData?.content}
                initialSets={modalData?.content?.sets}
                onChange={setTableState}
                coachCompletion={coachCompletion}
                completionOnly={completionOnly}
                isLeftAndRight={eachSide}
                trackRepCount={trackRepCount}
                trackVolumeLoad={trackVolumeLoad}
                forcePrUpdate={forcePRUpdate}
                disablePrUpdate={disablePRUpdate}
              />
              )}
              {isSAQC && (
              <ExerciseTableTransposedSAQCLocal
                content={modalData?.content}
                initialSets={modalData?.content?.sets}
                onChange={setTableState}
                coachCompletion={coachCompletion}
                completionOnly={completionOnly}
              />
              )}
            </ToggleControlledSection>
            {/* Toggle 2: Update Advanced Info */}
            {showAdditionalSection && (
            <ToggleSection>
              <TBToggleSwitch
                ariaLabel='Update additional info and tracking settings for future occurrences'
                checked={updateAdvancedInfoToggle}
                onChange={() => setUpdateAdvancedInfoToggle(!updateAdvancedInfoToggle)}
              />
              <ToggleLabelText>
                Make changes to additional info text, and exercise tracking settings for future occurrences
              </ToggleLabelText>
            </ToggleSection>
            )}
            {/* Additional Options Section */}
            <ToggleControlledSection data-toggle-section disabled={!updateAdvancedInfoToggle}>
              {showAdditionalSection ? (
                <>
                  {(isLift || isTag) && (
                  <CheckboxGrid>
                    {/* Track Volume Load */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={trackVolumeLoad}
                          onChange={(e) => setTrackVolumeLoad(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='track-volume-load-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='track-volume-load-checkbox'>
                        Track Volume Load
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Track Rep Count */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={trackRepCount}
                          onChange={(e) => setTrackRepCount(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='track-rep-count-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='track-rep-count-checkbox'>
                        Track Rep Count
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Each Side */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={eachSide}
                          onChange={(e) => setEachSide(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='each-side-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='each-side-checkbox'>
                        Each Side
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Force PR Update (mutually exclusive with Disable PR Update) */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={forcePRUpdate}
                          onChange={(e) => {
                            setForcePRUpdate(e.target.checked);
                            if (e.target.checked) setDisablePRUpdate(false);
                          }}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='force-pr-update-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='force-pr-update-checkbox'>
                        Force PR Update
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Disable PR Update (mutually exclusive with Force PR Update) */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={disablePRUpdate}
                          onChange={(e) => {
                            setDisablePRUpdate(e.target.checked);
                            if (e.target.checked) setForcePRUpdate(false);
                          }}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='disable-pr-update-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='disable-pr-update-checkbox'>
                        Disable PR Update
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Spacer to push next row to column 1 */}
                    <div />

                    {/* Completion Only */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={completionOnly}
                          onChange={(e) => setCompletionOnly(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='completion-only-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='completion-only-checkbox'>
                        Completion Only
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Coach Completion */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={coachCompletion}
                          onChange={(e) => setCoachCompletion(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='coach-completion-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='coach-completion-checkbox'>
                        Coach Completion
                      </CheckboxLabel>
                    </CheckboxContainer>
                  </CheckboxGrid>
                  )}
                  {isSAQC && (
                  <CheckboxGrid>
                    {/* Completion Only */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={completionOnly}
                          onChange={(e) => setCompletionOnly(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='completion-only-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='completion-only-checkbox'>
                        Completion Only
                      </CheckboxLabel>
                    </CheckboxContainer>

                    {/* Coach Completion */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={coachCompletion}
                          onChange={(e) => setCoachCompletion(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='coach-completion-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='coach-completion-checkbox'>
                        Coach Completion
                      </CheckboxLabel>
                    </CheckboxContainer>
                  </CheckboxGrid>
                  )}
                  {isCircuit && (
                  <CheckboxGrid>
                    {/* Coach Completion */}
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={coachCompletion}
                          onChange={(e) => setCoachCompletion(e.target.checked)}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='coach-completion-checkbox'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='coach-completion-checkbox'>
                        Coach Completion
                      </CheckboxLabel>
                    </CheckboxContainer>
                  </CheckboxGrid>
                  )}
                  {/* Additional Information textarea - only for lifts, tags, SAQ+Cs, and circuits */}
                  {(isLift || isTag || isSAQC || isCircuit) && (
                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                    <CheckboxLabel style={{ display: 'block', marginBottom: '8px' }}>
                      Additional Information
                    </CheckboxLabel>
                    <AdditionalInfoTextArea
                      value={additionalInfoText}
                      onChange={(e) => {
                        if (e.target.value.length <= 255) {
                          setAdditionalInfoText(e.target.value);
                        }
                      }}
                      maxLength={255}
                      placeholder='Add additional information for this exercise...'
                    />
                    <CharacterCount>
                      {additionalInfoText.length}
                      /255
                    </CharacterCount>
                  </div>
                  )}
                </>
              ) : null}
            </ToggleControlledSection>
            <ButtonsContainer data-modal-footer>
              <PrimaryButton onClick={handleNext} disabled={isFetchingCount} type='button'>
                {isFetchingCount ? 'LOADING...' : 'NEXT'}
              </PrimaryButton>
              <SecondaryButton onClick={handleBack} type='button'>BACK</SecondaryButton>
            </ButtonsContainer>
          </div>
        </animated.div>
      )}
      {showSlide3 && (
        <animated.div style={slide3Animation}>
          <SlideContent ref={slide3Ref} tabIndex={-1}>
            <ConfirmationText>
              {futureOccurrencesCount !== null ? (
                <>
                  You are about to change
                  {' '}
                  <HighlightText>
                    {futureOccurrencesCount}
                    {' '}
                    instances
                  </HighlightText>
                  {' '}
                  of
                  {' '}
                  <HighlightText>{modalData?.content?.title || 'the exercise'}</HighlightText>
                  {selectedExercise && selectedExercise.exerciseId !== modalData?.content?.exerciseId && (
                    <>
                      {' '}
                      to
                      {' '}
                      <HighlightText>{selectedExercise.name}</HighlightText>
                    </>
                  )}
                </>
              ) : (
                <>
                  You are about to update future occurrences of
                  {' '}
                  <HighlightText>{modalData?.content?.title || 'the exercise'}</HighlightText>
                  {selectedExercise && selectedExercise.exerciseId !== modalData?.content?.exerciseId && (
                    <>
                      {' '}
                      to
                      {' '}
                      <HighlightText>{selectedExercise.name}</HighlightText>
                    </>
                  )}
                </>
              )}
            </ConfirmationText>
            <ButtonsContainer data-modal-footer>
              <PrimaryButton disabled={isSubmitting} onClick={handleSubmit} type='button'>
                {isSubmitting ? 'UPDATING...' : 'CONFIRM'}
              </PrimaryButton>
              <SecondaryButton onClick={handleBack} type='button'>BACK</SecondaryButton>
            </ButtonsContainer>
          </SlideContent>
        </animated.div>
      )}

    </ModalContainer>
  );
};

UpdateFutureOccurrencesModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.instanceOf(Object).isRequired,
};

export default UpdateFutureOccurrencesModal;
